import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import {
  Building2, Bell, Users, FileText, MessageSquare, ShieldAlert, Plus, X, KeyRound, Save, Lock,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const DOC_STATUS_OPTIONS = ["Received", "Missing", "Pending", "Not Required", "Uploaded"];
const fieldCls = "h-9 w-full px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-70 disabled:bg-surface-alt";

function SectionCard({ icon: Icon, title, desc, children }) {
  return (
    <div className="bg-surface border border-line rounded-md overflow-hidden">
      <div className="px-5 py-3.5 border-b border-line flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-steel" strokeWidth={1.75} />
        <div>
          <h3 className="font-heading text-[15px] font-semibold text-ink">{title}</h3>
          {desc && <p className="text-[12px] text-ink-3">{desc}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="op-label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function AddUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "operator", password: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password are required.");
    try {
      await client.post("/users", form);
      toast.success("Teammate added");
      setOpen(false);
      setForm({ name: "", email: "", role: "operator", password: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add user.");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-user-btn" className="h-9 px-3.5 rounded-sm bg-brand text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-brand-dark transition-colors">
          <Plus className="h-4 w-4" /> Add Teammate
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Teammate</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">Create an account with a temporary password. Share it with them to sign in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 py-1">
          <Labeled label="Full name"><input data-testid="user-name-input" className={fieldCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></Labeled>
          <Labeled label="Email"><input data-testid="user-email-input" type="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></Labeled>
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Role">
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role-select" className="h-9 border-line rounded-sm text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </Labeled>
            <Labeled label="Temporary password"><input data-testid="user-password-input" className={fieldCls} value={form.password} onChange={(e) => set("password", e.target.value)} /></Labeled>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="user-submit-btn" onClick={submit} className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-dark transition-colors">Create</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [newDoc, setNewDoc] = useState("");
  const [newRisk, setNewRisk] = useState("");

  const loadUsers = () => client.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    client.get("/settings").then((r) => setS(r.data));
    loadUsers();
  }, []);

  if (user && user.role === "operator") {
    return (
      <Layout>
        <div className="bg-surface border border-line rounded-md p-12 text-center">
          <Lock className="h-6 w-6 text-ink-3 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-medium text-ink">Settings are restricted</h2>
          <p className="text-sm text-ink-2 mt-1">Only Admin and Manager roles can access Settings.</p>
        </div>
      </Layout>
    );
  }

  if (!s) return <Layout><div className="text-sm text-ink-3 font-mono-data py-20 text-center">Loading settings…</div></Layout>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setTemplate = (stage, v) => setS((p) => ({ ...p, update_templates: { ...p.update_templates, [stage]: v } }));
  const setDocDefault = (t, v) => setS((p) => ({ ...p, doc_defaults: { ...p.doc_defaults, [t]: v } }));

  const addDocType = () => {
    const t = newDoc.trim();
    if (!t || s.doc_types.includes(t)) return;
    setS((p) => ({ ...p, doc_types: [...p.doc_types, t], doc_defaults: { ...p.doc_defaults, [t]: "Not Required" } }));
    setNewDoc("");
  };
  const removeDocType = (t) => setS((p) => {
    const dd = { ...p.doc_defaults }; delete dd[t];
    return { ...p, doc_types: p.doc_types.filter((x) => x !== t), doc_defaults: dd };
  });
  const addRisk = () => {
    const r = newRisk.trim();
    if (!r || s.risk_statuses.includes(r)) return;
    setS((p) => ({ ...p, risk_statuses: [...p.risk_statuses, r] }));
    setNewRisk("");
  };
  const removeRisk = (r) => setS((p) => ({ ...p, risk_statuses: p.risk_statuses.filter((x) => x !== r) }));

  const save = async () => {
    try {
      const { can_edit, id, ...payload } = s;
      const r = await client.put("/settings", payload);
      setS(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save settings.");
    }
  };

  const editUser = async (u, changes) => {
    try {
      const r = await client.put(`/users/${u.id}`, changes);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? r.data : x)));
      toast.success("User updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update user.");
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new temporary password for ${u.name}:`);
    if (!pw) return;
    try {
      await client.post(`/users/${u.id}/reset-password`, { password: pw });
      toast.success("Password reset");
    } catch (e) {
      toast.error("Could not reset password.");
    }
  };

  const STAGES_FOR_TEMPLATES = ["New Job", "Pickup Scheduled", "Picked Up", "In Transit", "At Facility", "Ready for Delivery", "Delivered", "Ready to Close", "Closed", "Delayed"];

  return (
    <Layout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="op-label mb-1">Configuration</p>
          <h1 className="font-heading text-2xl font-semibold text-ink">Settings</h1>
          <p className="text-sm text-ink-2 mt-1">{isAdmin ? "Manage your company profile, team, and operational defaults." : "Read-only view — only Admins can edit settings."}</p>
        </div>
        {isAdmin && (
          <button data-testid="save-settings-btn" onClick={save} className="h-9 px-4 rounded-sm bg-brand text-white text-[13.5px] font-medium flex items-center gap-1.5 hover:bg-brand-dark transition-colors">
            <Save className="h-4 w-4" /> Save Changes
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Company Profile */}
        <SectionCard icon={Building2} title="Company Profile" desc="How your operation identifies itself.">
          <div className="grid sm:grid-cols-2 gap-3.5">
            <Labeled label="Company name"><input data-testid="company-name-input" className={fieldCls} disabled={!isAdmin} value={s.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></Labeled>
            <Labeled label="Initials / Logo mark"><input className={fieldCls} disabled={!isAdmin} value={s.initials || ""} onChange={(e) => set("initials", e.target.value)} /></Labeled>
            <Labeled label="Timezone"><input className={fieldCls} disabled={!isAdmin} value={s.timezone || ""} onChange={(e) => set("timezone", e.target.value)} /></Labeled>
            <Labeled label="Default currency"><input className={fieldCls} disabled={!isAdmin} value={s.currency || ""} onChange={(e) => set("currency", e.target.value)} /></Labeled>
            <Labeled label="Main contact name"><input className={fieldCls} disabled={!isAdmin} value={s.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></Labeled>
            <Labeled label="Main contact email"><input className={fieldCls} disabled={!isAdmin} value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Labeled>
            <Labeled label="Main phone"><input className={fieldCls} disabled={!isAdmin} value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Labeled>
            <Labeled label="Service area / region"><input className={fieldCls} disabled={!isAdmin} value={s.service_area || ""} onChange={(e) => set("service_area", e.target.value)} /></Labeled>
            <div className="sm:col-span-2">
              <Labeled label="Company address"><input className={fieldCls} disabled={!isAdmin} value={s.address || ""} onChange={(e) => set("address", e.target.value)} /></Labeled>
            </div>
          </div>
        </SectionCard>

        {/* Notification Thresholds */}
        <SectionCard icon={Bell} title="Notification Thresholds" desc="Defaults that drive update reminders and stale-job alerts.">
          <div className="grid sm:grid-cols-2 gap-3.5">
            <Labeled label="Customer update frequency (hours)"><input data-testid="update-frequency-input" type="number" className={fieldCls} disabled={!isAdmin} value={s.update_frequency_hours ?? ""} onChange={(e) => set("update_frequency_hours", Number(e.target.value))} /></Labeled>
            <Labeled label="No-update alert threshold (hours)"><input type="number" className={fieldCls} disabled={!isAdmin} value={s.no_update_threshold_hours ?? ""} onChange={(e) => set("no_update_threshold_hours", Number(e.target.value))} /></Labeled>
          </div>
          <p className="text-[12px] text-ink-3 mt-3">Jobs with no update beyond the threshold are surfaced in Needs Attention.</p>
        </SectionCard>

        {/* Document Checklist Defaults */}
        <SectionCard icon={FileText} title="Document Defaults" desc="Default status applied to each document type on a new job.">
          <div className="divide-y divide-line -my-1">
            {s.doc_types.map((t) => (
              <div key={t} data-testid={`doc-default-${t.replace(/\s+/g, "-")}`} className="flex items-center justify-between py-2.5 gap-2">
                <span className="text-[13.5px] text-ink">{t}</span>
                <div className="flex items-center gap-2">
                  <Select value={s.doc_defaults?.[t] || "Not Required"} disabled={!isAdmin} onValueChange={(v) => setDocDefault(t, v)}>
                    <SelectTrigger className="h-8 w-[140px] rounded-sm border-line text-[12.5px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o} className="text-[12.5px]">{o}</SelectItem>)}</SelectContent>
                  </Select>
                  {isAdmin && <button onClick={() => removeDocType(t)} className="text-ink-3 hover:text-danger"><X className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2 mt-3">
              <input className={fieldCls} placeholder="Add a document type…" value={newDoc} onChange={(e) => setNewDoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDocType()} />
              <button onClick={addDocType} className="h-9 px-3 rounded-sm border border-line bg-surface-alt text-[13px] font-medium hover:bg-line/40 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
            </div>
          )}
        </SectionCard>

        {/* Risk Options */}
        <SectionCard icon={ShieldAlert} title="Risk / Status Options" desc="Risk values available on shipment dropdowns and badges.">
          <div className="flex flex-wrap gap-2">
            {s.risk_statuses.map((r) => (
              <span key={r} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm bg-surface-alt border border-line text-[13px] text-ink">
                {r}
                {isAdmin && <button onClick={() => removeRisk(r)} className="text-ink-3 hover:text-danger"><X className="h-3.5 w-3.5" /></button>}
              </span>
            ))}
          </div>
          {isAdmin && (
            <div className="flex gap-2 mt-3">
              <input className={fieldCls} placeholder="Add a risk status…" value={newRisk} onChange={(e) => setNewRisk(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRisk()} />
              <button onClick={addRisk} className="h-9 px-3 rounded-sm border border-line bg-surface-alt text-[13px] font-medium hover:bg-line/40 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
            </div>
          )}
        </SectionCard>

        {/* Customer Update Templates */}
        <SectionCard icon={MessageSquare} title="Customer Update Templates" desc="These power the suggested message on each shipment by stage.">
          <div className="space-y-3 max-h-[420px] overflow-y-auto mv-scroll pr-1">
            {STAGES_FOR_TEMPLATES.map((stage) => (
              <div key={stage}>
                <label className="op-label block mb-1">{stage}</label>
                <textarea
                  data-testid={`template-${stage.replace(/\s+/g, "-")}`}
                  rows={2}
                  disabled={!isAdmin}
                  value={s.update_templates?.[stage] || ""}
                  onChange={(e) => setTemplate(stage, e.target.value)}
                  className="w-full rounded-sm border border-line bg-surface p-2.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-brand resize-none disabled:bg-surface-alt disabled:opacity-70"
                />
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Team Users */}
        <SectionCard icon={Users} title="Team Users" desc={isAdmin ? "Create teammates and manage roles and access." : "Your team and their roles."}>
          {isAdmin && <div className="flex justify-end mb-3"><AddUserDialog onCreated={loadUsers} /></div>}
          <div className="border border-line rounded-sm overflow-hidden">
            <table className="w-full text-left">
              <thead><tr className="bg-surface-alt border-b border-line">
                {["Name", "Role", "Status", isAdmin ? "Actions" : ""].map((h) => <th key={h} className="op-label px-3 py-2">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-line">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="px-3 py-2.5">
                      <div className="text-[13.5px] text-ink font-medium">{u.name}</div>
                      <div className="text-[11.5px] text-ink-3 font-mono-data">{u.email}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      {isAdmin ? (
                        <Select value={u.role} onValueChange={(v) => editUser(u, { role: v })}>
                          <SelectTrigger data-testid={`user-role-${u.email}`} className="h-8 w-[110px] rounded-sm border-line text-[12.5px] capitalize"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="operator">Operator</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : <span className="text-[13px] text-ink-2 capitalize">{u.role}</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-sm font-medium ${u.active ? "bg-[#E9EEF3] text-ok" : "bg-[#ECEFF2] text-ink-3"}`}>{u.active ? "Active" : "Inactive"}</span>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <button data-testid={`toggle-active-${u.email}`} onClick={() => editUser(u, { active: !u.active })} className="text-[12px] text-steel hover:text-brand">{u.active ? "Deactivate" : "Reactivate"}</button>
                          <button onClick={() => resetPassword(u)} className="text-ink-3 hover:text-brand" title="Reset password"><KeyRound className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>
    </Layout>
  );
}
